import Container from '@mui/material/Container';
import React, { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCriticalIncidentCases } from 'api/queries/incidentReportQuery';
import { type CICase, CICasesFilterValue } from 'components/CriticalIncident/interfaces';
import { Spinner } from 'components/Shared';
import { FullScreenMessage } from 'components/Shared/FullScreenMessage';
import {
    derivePaginationDetails,
    type ExtendedDataRequest,
    MaterialTable,
    MaterialTableFilterBar,
    Paginator,
} from 'components/Shared/MaterialTable';
import { MobileCICasesList } from '../MobileLayout/MobileCICasesList';

import { CICard } from '../../Shared/CICard/CICard';
import { useCICaseColumnsDefinitons } from '../hooks/useCICaseColumnsDefinitions';
import { useScreenSize } from 'components/Layout';
import moment from 'moment';
import { sortCICases } from 'components/CriticalIncident/utils/CIReferralUtils';
import { COUNTRY_CODE, useProviderCountryCode } from 'utils/regionUtils';

import './CICasesTable.scss';

export const CICasesTable: FC = () => {
    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });

    const { data: ciCases, isLoading } = useGetCriticalIncidentCases();

    const [filteredData, setFilteredData] = useState<CICase[] | undefined>(ciCases);

    const isAustralianProvider = useProviderCountryCode() === COUNTRY_CODE.AUSTRALIA;

    const { isMobile } = useScreenSize();

    const pageSize = isMobile ? 5 : 10;

    const filterUpcoming = useCallback((ciCase: CICase) => {
        return moment().isSameOrBefore(moment(ciCase.interventionStartDateTime));
    }, [ciCases?.length, isLoading]);

    const filterHistory = useCallback((ciCase: CICase) => {
        return moment().isAfter(moment(ciCase.interventionStartDateTime));
    }, [ciCases?.length, isLoading]);

    const filterOptions = [
        { label: t('all'), value: CICasesFilterValue.All },
        { label: t('upcoming'), value: CICasesFilterValue.UPCOMING },
        { label: t('history'), value: CICasesFilterValue.HISTORY }
    ]

    const [dataRequest, setDataRequest] = useState<ExtendedDataRequest>({
        search: '',
        take: pageSize,
        skip: 0,
        filterConfig: {
            field: 'interventionStartDateTime',
            options: filterOptions,
            value: CICasesFilterValue.All,
            filterBy: {
                [CICasesFilterValue.UPCOMING]: filterUpcoming,
                [CICasesFilterValue.HISTORY]: filterHistory
            }
        },
        searchFields: ['externalWorkOrderId'],
        sorts: {
            interventionStartDateTime: 'asc'
        }
    });

    const columns = useCICaseColumnsDefinitons();

    const sortedData = useMemo(() => sortCICases(filteredData, dataRequest.sorts.interventionStartDateTime), [filteredData, dataRequest.sorts]);

    const pagedData = useMemo(
        () => sortedData?.slice(isMobile ? 0 : dataRequest.skip, dataRequest.skip + dataRequest.take),
        [sortedData, dataRequest.skip, dataRequest.take, isMobile]
    );

    const paginationDetails = derivePaginationDetails(sortedData?.length, dataRequest);

    const handlePageChange = (currentPage: number) => {
        setDataRequest({ ...dataRequest, skip: (currentPage - 1) * dataRequest.take });
    };

    useEffect(() => {
        setDataRequest((prev) => ({
            ...prev,
            filterConfig: {
                ...prev.filterConfig!,
                options: filterOptions
            },
            searchFields: isAustralianProvider ? ['externalWorkOrderId', 'externalCaseId'] : ['externalWorkOrderId'],
            take: pageSize,
            skip: 0, //Resets to page 1
        }));
    }, [isMobile, dataRequest.filterConfig?.value, t, dataRequest.search, isAustralianProvider]);

    const emptyStateMessage = useMemo(() => {
        let message = t('ci_cases_table_no_rows');

        if (ciCases && ciCases.length > 0 && pagedData.length === 0) {
            if (dataRequest.filterConfig?.value === CICasesFilterValue.UPCOMING) {
                message = t('ci_cases_table_no_upcoming_cases');
            }

            if (dataRequest.filterConfig?.value === CICasesFilterValue.HISTORY) {
                message = t('ci_cases_table_no_history_cases');
            }

            if (dataRequest.search) {
                message = t('no_records_match_work_order_id');
            }
        }

        return message;
    }, [t, translationsReady, ciCases, pagedData, dataRequest.filterConfig?.value, dataRequest.search]);

    return (
        <div className="ci_cases_table_view_wrapper">
            {translationsReady ? (
                <React.Fragment>
                    <Container>
                        <div className="ci_cases_header">
                            <h1>{t('ci_cases')}</h1>
                            <MaterialTableFilterBar
                                data={ciCases || []}
                                dataRequest={dataRequest}
                                onDataRequestChange={setDataRequest}
                                onFilterData={setFilteredData}
                                searchPlaceholder={t('search_CIReferrals_by_workorder_id_placeholder')}
                            />
                        </div>
                    </Container>
                    {isMobile ? (
                        <MobileCICasesList
                            referrals={pagedData}
                            paginationDetails={paginationDetails}
                            handlePageChange={handlePageChange}
                            dataLoading={isLoading}
                            emptyStateMessage={emptyStateMessage}
                        />
                    ) : (
                        <Container>
                            <CICard elevation={2} className="table_container has_filter">
                                <MaterialTable
                                    dataLoading={isLoading}
                                    columns={columns}
                                    data={pagedData}
                                    emptyStateMessage={<FullScreenMessage title={emptyStateMessage} />}
                                />
                            </CICard>
                            {pagedData && pagedData.length > 0 && (
                                <Paginator paginationDetails={paginationDetails} onPageChange={handlePageChange} />
                            )}
                        </Container>
                    )}
                </React.Fragment>
            ) : (
                <Spinner fillParent />
            )}
        </div>
    );
};
